import React, { useEffect, useState } from "react";
import { Input, Button, Upload, Avatar, Modal, message } from "antd";
import { UserOutlined, UploadOutlined, EditOutlined } from "@ant-design/icons";
import ChangePasswordTab from "../../Components/ChangePasswordTab/ChangePasswordTab";
import "./ProfileSetting.css";
import axiosInstance from "../../Config/axiosConfig";

const ProfileSetting = ({ setLoggedIn, deviceId, userIp }) => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [profileImage, setProfileImage] = useState(null);
  const [userData, setUserData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [tempImage, setTempImage] = useState(null);
  const [tempName, setTempName] = useState("");

  // Modal state
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);

  // Track original data for comparison
  const [originalData, setOriginalData] = useState({
    name: "",
    photo: null
  });

  // Check if changes have been made
  const hasChanges = () => {
    // Check name changes
    const nameChanged = tempName !== originalData.name;
    
    // Check photo changes
    let photoChanged = false;
    
    if (tempImage === null && originalData.photo !== null) {
      // Photo was removed
      photoChanged = true;
    } else if (tempImage instanceof File || tempImage instanceof Blob) {
      // New photo was uploaded
      photoChanged = true;
    } else if (tempImage !== originalData.photo) {
      // Photo changed in some other way
      photoChanged = true;
    }
    
    return nameChanged || photoChanged;
  };

  const isSaveDisabled = !hasChanges() || isLoading;

  // Alternative 3: Custom file handler without Antd Upload issues
  const handleCustomFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        message.error('Please select an image file (JPEG, PNG, etc.)');
        return;
      }

      // Validate file size (e.g., 5MB max)
      if (file.size > 5 * 1024 * 1024) {
        message.error('Image size should be less than 5MB');
        return;
      }

      setTempImage(file);
      
      // Create preview using FileReader (more reliable)
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfileImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = () => {
    setTempImage(null);
    setProfileImage(null);
    localStorage.removeItem('userPhoto');
    message.success('Photo removed');
  };

  useEffect(() => {
    const loadUserData = () => {
      try {
        const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
        const userPhoto = localStorage.getItem('userPhoto');
        
        if (userInfo) {
          setUserData(userInfo);
          setTempName(userInfo.name || "");
          setName(userInfo.name || "");
          setPhone(userInfo.username || "");
          
          // Set original data for comparison
          setOriginalData({
            name: userInfo.name || "",
            photo: userPhoto || null
          });
        }
        
        if (userPhoto && isValidBase64(userPhoto)) {
          setTempImage(userPhoto);
          setProfileImage(userPhoto);
        }
      } catch (error) {
        console.error('Error loading user data:', error);
      }
    };

    loadUserData();
  }, []);

  const isValidBase64 = (str) => {
    try {
      return str.startsWith('data:image');
    } catch (e) {
      return false;
    }
  };

  const handleCancel = () => {
    setTempName(originalData.name);
    // Reset to original image
    setTempImage(originalData.photo);
    setProfileImage(originalData.photo);
    message.info('Changes cancelled');
  };

  const handleSave = async () => {
    if (!tempName.trim()) {
      message.error('Please enter your name');
      return;
    }

    if (!hasChanges()) {
      message.info('No changes to save');
      return;
    }

    setIsLoading(true);
    try {
      const accessToken = localStorage.getItem("accessToken");
      const storedDeviceId = localStorage.getItem("deviceId");
      const storedUserIp = localStorage.getItem("userIp");
      
      const formData = new FormData();
      formData.append('userId', userData.userId);
      formData.append('name', tempName);
      formData.append('instructorTitle', userData.instructorTitle || '');
      formData.append('aboutInstructor', userData.aboutInstructor || '');
      
      // Handle profile image
      if (tempImage instanceof File || tempImage instanceof Blob) {
        formData.append('userPhotoFile', tempImage);
      } else if (tempImage === null && originalData.photo !== null) {
        // Only send remove photo if there was originally a photo
        formData.append('removePhoto', 'true');
      }

      const response = await axiosInstance.post('/user/update-profile', formData, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "multipart/form-data",
          UserDevice: storedDeviceId,
          UserIPAddress: storedUserIp,
        },
      });

      if (response.data.code === 200) {
        message.success('Profile updated successfully');
        
        const updatedUserInfo = { 
          ...userData,
          name: response.data.data.name,
          instructorTitle: response.data.data.instructor_title,
          aboutInstructor: response.data.data.about_instructor,
        };
        
        setUserData(updatedUserInfo);
        setName(tempName);
        localStorage.setItem("userInfo", JSON.stringify(updatedUserInfo));
        
        // Handle profile image update
        let newPhoto = null;
        if (response.data.data.photo) {
          newPhoto = response.data.data.photo.startsWith('data:image') 
            ? response.data.data.photo 
            : `data:image/jpeg;base64,${response.data.data.photo}`;
          
          localStorage.setItem('userPhoto', newPhoto);
          setTempImage(newPhoto);
          setProfileImage(newPhoto);
        } else {
          localStorage.removeItem('userPhoto');
          setTempImage(null);
          setProfileImage(null);
          newPhoto = null;
        }

        // Update original data after successful save
        setOriginalData({
          name: tempName,
          photo: newPhoto
        });
      }
    } catch (error) {
      message.error('Error updating profile');
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getAvatarSrc = () => {
    // Handle base64 strings (from localStorage or FileReader)
    if (profileImage && typeof profileImage === 'string') {
      return profileImage;
    }
    return null;
  };

  const hasPhoto = profileImage !== null;

    //-------------Handle Change password----------------------
  const handleOpenPasswordModal = () => {
    setIsPasswordModalOpen(true);
  };

  return (
    <>
      <div className="profile-settings-account-form">

        {/* LEFT: FORM */}
        <div className="profile-settings-account-forms-layout">
          
          {/* Name - Always Editable */}
          <div className="profile-settings-form-group">
            <label className="profile-settings-label">Your Name</label>
            <Input
              value={tempName}
              onChange={(e) => setTempName(e.target.value)}
              className="profile-settings-input"
              placeholder="Enter your name"
            />
          </div>

          {/* Password */}
          <div className="profile-settings-form-group">
            <label className="profile-settings-label">Password</label>
            <Input
              type="tel"
              placeholder="********"
              value={""}
              disabled
              className="profile-settings-input"
            />
            <div
              className="profile-settings-change-button"
              onClick={() => handleOpenPasswordModal(true)}
            >
              <span className="profile-setting-change-text">Change Password</span>
            </div>
          </div>

          {/* Phone */}
          <div className="profile-settings-form-group">
            <label className="profile-settings-label">User Account</label>
            <Input
              type="tel"
              placeholder="+255 620 2297 13"
              value={userData.username || ""}
              disabled
              className="profile-settings-input"
            />
          </div>

          {/* Submit Buttons - Always Visible */}
          <div className="profile-settings-submit">
            <button 
              type="button" 
              className="profile-settings-submit-btn-cancel"
              onClick={handleCancel}
              disabled={isLoading || !hasChanges()}
            >
              Cancel
            </button>
            <button 
              type="button" 
              className="profile-settings-submit-btn"
              onClick={handleSave}
              disabled={isSaveDisabled}
            >
              {isLoading ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>

        {/* RIGHT: PHOTO */}
        <div className="profile-photo-container">
          <Avatar
            size={140}
            src={getAvatarSrc()}
            icon={<UserOutlined />}
            className="profile-avatar"
          />

          <div className="photo-actions">
            {hasPhoto ? (
              <>                
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleCustomFileSelect}
                  style={{ display: 'none' }}
                  id="file-input"
                />
                <Button 
                  icon={<UploadOutlined />} 
                  className="photo-action-btn"
                  onClick={() => document.getElementById('file-input').click()}
                >
                  Change Photo
                </Button>
                
                <Button 
                  type="text" 
                  danger 
                  onClick={handleRemovePhoto}
                  className="remove-photo-btn"
                >
                  Remove Photo
                </Button>
              </>
            ) : (
              <>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleCustomFileSelect}
                  style={{ display: 'none' }}
                  id="file-input-upload"
                />
                <Button 
                  icon={<UploadOutlined />} 
                  className="photo-action-btn"
                  onClick={() => document.getElementById('file-input-upload').click()}
                >
                  Upload Photo
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* PASSWORD MODAL */}
  <Modal
        title="Change Password"
        open={isPasswordModalOpen}
        onCancel={() => setIsPasswordModalOpen(false)}
        footer={null}
      >
        <ChangePasswordTab 
          username={userData.username}
          deviceId={deviceId}
          userIp={userIp}
          onSuccess={() => setIsPasswordModalOpen(false)}
        />
      </Modal>
    </>
  );
};

export default ProfileSetting;